<script setup lang="ts">
import type { DependentFieldDependentField, DropdownField, EmailField, MobilePhoneField, MultiLineTextField, MultipleCheckboxesField, NumberField, PhoneField, SingleCheckboxField, SingleLineTextField } from '@hubspot/api-client/lib/codegen/marketing/forms'
import type { SerializeObject } from 'nitropack'

// TODO: Add support for dropdowns, radio buttons
const props = defineProps<{
  field: SerializeObject<
    | DependentFieldDependentField
    | SingleLineTextField
    | MultiLineTextField
    | EmailField
    | PhoneField
    | MobilePhoneField
    | NumberField
    | SingleCheckboxField
    | MultipleCheckboxesField
    | DropdownField
  >
}>()

const model = defineModel<string | number | undefined>()

const multipleModel = defineModel<string[]>('multiple', { default: () => [] })

const type = computed(() => {
  switch (props.field.fieldType) {
    case 'email':
      return 'email'
    case 'phone':
    case 'mobile_phone':
      return 'tel'
    case 'number':
      return 'number'
    case 'single_line_text':
      return 'text'
    default:
      return null
  }
})

const dependentFields = computed(
  () => props.field.dependentFields
    ?.filter(showDependentField(model.value, multipleModel.value))
    ?.map(it => it.dependentField),
)
</script>

<template>
  <div v-if="!field.hidden" class="flex flex-col gap-s">
    <FormInput
      v-if="type"
      :id="`${field.name}-input`"
      v-model="model"
      :type="type"
      :name="field.name"
      :label="field.label"
      :required="field.required"
    />

    <FormTextarea
      v-else-if="field.fieldType === 'multi_line_text'"
      :id="`${field.name}-textarea`"
      v-model="model"
      :type="type"
      :name="field.name"
      :label="field.label"
      :required="field.required"
    />

    <label
      v-else-if="field.fieldType === 'single_checkbox'"
      :class="checkboxVariants.label({ disabled: false })"
    >
      <input v-model="model" :name="field.name" :required="field.required" type="checkbox" :class="checkboxVariants.input()">
      <span class="text-small flex gap-px">
        <slot name="label">{{ field.label }}</slot>
        <span v-if="field.required" class="text-red-500">*</span>
      </span>
    </label>

    <div v-else-if="field.fieldType === 'multiple_checkboxes'" class="flex flex-col gap-s">
      <span v-if="field.label" class="text-small flex gap-px">
        <slot name="label">{{ field.label }}</slot>
        <span v-if="field.required" class="text-red-500">*</span>
      </span>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-y-2xs gap-x-m">
        <label
          v-for="option in (field as MultipleCheckboxesField).options"
          :key="option.value"
          :class="checkboxVariants.label({ disabled: false })"
        >
          <!-- TODO: Required -->
          <input
            v-model="multipleModel"
            type="checkbox"
            :name="field.name"
            :value="option.value"
            :class="checkboxVariants.input()"
          >
          <span class="text-small" v-text="option.label" />
        </label>
      </div>
    </div>

    <HubspotFormSelect v-else-if="field.fieldType === 'dropdown'" :field="field as DropdownField" />

    <HubspotFormField v-for="dependentField in dependentFields" :key="dependentField.name" :field="dependentField" />
  </div>
</template>
